(function () {
  "use strict";

  // Timing constants - exactly matching React component
  const TIMING = {
    typing: { min: 85, max: 150 },
    transition: {
      scaleOut: 700,
      toSide: 1100,
      outDuration: 800,
      toCenter: 1100,
      scaleIn: 750,
      scaleInDelay: 350,
      inDuration: 1100,
    },
    sequence: { beforePositionUpdate: 70, finalReset: 100 },
    ui: { cursorBlink: 1400, autoScroll: 4500 },
  };

  // Transition state classes - exactly matching React component
  const TRANSITIONS = {
    "left-stable": {
      classes: "-translate-x-full translate-y-1/5 scale-[0.4]",
      style: { cursor: "pointer" },
    },
    "center-stable": {
      classes: "translate-x-0 -translate-y-0 scale-100",
      style: {},
    },
    "right-stable": {
      classes: "translate-x-full translate-y-1/5 scale-[0.4]",
      style: { cursor: "pointer" },
    },
    "out-stable": {
      classes: "translate-x-full translate-y-1/5 scale-0",
      style: {},
    },
    "left-scaling-out": {
      classes: "-translate-x-full translate-y-1/5 scale-0",
      style: {
        transition: "scale",
        transitionDuration: `${TIMING.transition.scaleOut}ms`,
      },
    },
    "right-scaling-in": {
      classes: "translate-x-full translate-y-1/5 scale-[0.4]",
      style: {
        transition: "scale",
        transitionDuration: `${TIMING.transition.scaleIn}ms`,
        transitionDelay: `${TIMING.transition.scaleInDelay}ms`,
      },
    },
    "right-scaling-out": {
      classes: "translate-x-full translate-y-1/5 scale-0",
      style: {
        transition: "scale",
        transitionDuration: `${TIMING.transition.scaleOut}ms`,
      },
    },
    "left-scaling-in": {
      classes: "-translate-x-full translate-y-1/5 scale-[0.4]",
      style: {
        transition: "scale",
        transitionDuration: `${TIMING.transition.scaleIn}ms`,
        transitionDelay: `${TIMING.transition.scaleInDelay}ms`,
      },
    },
    "left-to-right": {
      classes: "translate-x-full scale-0 duration-0",
      style: { transitionDuration: "0ms" },
    },
    "center-to-left": {
      classes: "-translate-x-full translate-y-1/5 scale-[0.4]",
      style: {
        transition: "all",
        transitionDuration: `${TIMING.transition.toSide}ms`,
      },
    },
    "to-center": {
      classes: "translate-x-0 scale-100",
      style: {
        transition: "all",
        transitionDuration: `${TIMING.transition.toCenter}ms`,
      },
    },
    "right-to-left": {
      classes: "-translate-x-full scale-0 duration-0",
      style: { transitionDuration: "0ms" },
    },
    "center-to-right": {
      classes: "translate-x-full translate-y-1/5 scale-[0.4]",
      style: {
        transition: "all",
        transitionDuration: `${TIMING.transition.toSide}ms`,
      },
    },
  };

  // Inline style properties the transition states set; reset between states.
  const STATE_STYLE_PROPS = [
    "transition",
    "transitionDuration",
    "transitionDelay",
    "cursor",
  ];

  function debounce(fn, wait = 150) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  class GridCarousel {
    constructor() {
      this.initializeElements();
      this.loadConfig();
      this.initializeState();
      this.setupGrid();
      this.setupEventListeners();
      this.start();
    }

    initializeElements() {
      this.section = document.querySelector("[data-carousel-section]");
      if (!this.section) return;

      this.bgTextElement = this.section.querySelector("[data-bg-text-content]");
      this.cursor = this.section.querySelector("[data-cursor]");
      this.contentArea = this.section.querySelector("[data-content-area]");
      this.titleElement = this.section.querySelector("[data-slide-title]");
      this.subtitleElement = this.section.querySelector(
        "[data-slide-subtitle]"
      );
      this.buttonCaptionElement = this.section.querySelector(
        "[data-button-caption]"
      );
      this.buttonContainer = this.section.querySelector(
        "[data-carousel-button-container]"
      );
      this.buttonElement = this.section.querySelector("[data-carousel-button]");
      this.gridContainer = this.section.querySelector("[data-grid-container]");
      this.gridPattern = this.section.querySelector("[data-grid-pattern]");

      this.imageElements = Array.from(
        this.section.querySelectorAll("[data-carousel-img]")
      );
    }

    loadConfig() {
      const configScript = document.querySelector("[data-carousel-config]");
      if (!configScript) return;

      try {
        this.config = JSON.parse(configScript.textContent);
        this.slides = this.config.slides || [];

        // Handle slide duplication for 2-slide case
        if (this.slides.length === 2) {
          this.slides.push(this.slides[0]);
        }
      } catch (e) {
        console.error("Failed to parse carousel config:", e);
        this.slides = [];
      }
    }

    initializeState() {
      this.currentSlideIndex = 0;
      this.bgText = this.slides[0]?.bgText || "";
      this.autoScrollEnabled = true;
      this.isTransitioning = false;
      this.transitionContent = false;

      // Image transition states - [left, center, right, out]
      this.imageTransitions = {
        0: "left-stable",
        1: "center-stable",
        2: "right-stable",
        3: "out-stable",
      };

      // Track which img element is in which position: [left, center, right, out]
      this.imagePositions = [0, 1, 2, 3];

      // Initialize image sources
      this.imageSources = ["", "", "", ""];
      if (this.slides.length >= 3) {
        const currentIndex = 0;
        const prevIndex =
          (currentIndex - 1 + this.slides.length) % this.slides.length;
        const nextIndex = (currentIndex + 1) % this.slides.length;
        const futureIndex = (nextIndex + 1) % this.slides.length;

        this.imageSources[0] = this.slides[prevIndex]?.image || "";
        this.imageSources[1] = this.slides[currentIndex]?.image || "";
        this.imageSources[2] = this.slides[nextIndex]?.image || "";
        this.imageSources[3] = this.slides[futureIndex]?.image || "";
      }

      this.updateImageElements();
      this.updateContent();
    }

    setupGrid() {
      if (!this.config.showGrid || !this.gridPattern) return;

      this.setupContentSizer();

      const calculateGridDimensions = () => {
        if (!this.section) return;

        const containerRect = this.section.getBoundingClientRect();
        const tileSize = 72;

        // Calculate the number of complete tiles that fit
        const tilesHeight = Math.floor(containerRect.height / tileSize);

        // Calculate perfect grid dimensions - add 1 for the final grid line
        const perfectHeight = tilesHeight * tileSize + 1;

        this.gridPattern.style.width = "100%";
        this.gridPattern.style.height = `${perfectHeight}px`;
        this.gridPattern.style.backgroundImage = `
          linear-gradient(to right, var(--grid-line) 1px, transparent 1px),
          linear-gradient(to bottom, var(--grid-line) 1px, transparent 1px)`;
        this.gridPattern.style.backgroundSize = `${tileSize}px ${tileSize}px`;
        this.gridPattern.style.backgroundPosition = "0 0";
        this.gridPattern.style.backgroundRepeat = "repeat";
      };

      // Initial calculation
      calculateGridDimensions();

      // Recalculate on window resize
      const handleResize = () => calculateGridDimensions();
      window.addEventListener("resize", handleResize);

      // Store cleanup function
      this.gridCleanup = () => {
        window.removeEventListener("resize", handleResize);
      };
    }

    setupContentSizer() {
      if (!this.section || !this.contentArea) return;
      // Run once now, and on resize
      const run = () => this.calculateAndApplyContentMinHeight();
      run();
      this._contentResizeHandler = debounce(run, 150);
      window.addEventListener("resize", this._contentResizeHandler);
      // Cleanup hook
      this.contentSizerCleanup = () => {
        window.removeEventListener("resize", this._contentResizeHandler);
      };
    }

    calculateAndApplyContentMinHeight() {
      if (!this.slides || !this.slides.length || !this.contentArea) return;

      // Build a hidden measurer cloned from content area so layout/styles match
      const measurer = this.contentArea.cloneNode(true);
      // Make sure it's not participating in layout/paint
      Object.assign(measurer.style, {
        position: "absolute",
        visibility: "hidden",
        pointerEvents: "none",
        inset: "auto",
        left: "-99999px",
        top: "0",
        width: this.contentArea.offsetWidth + "px", // match current width
        height: "auto",
        minHeight: "0",
        maxHeight: "none",
        opacity: "1",
      });

      document.body.appendChild(measurer);
      const mTitle = measurer.querySelector("[data-slide-title]");
      const mSub = measurer.querySelector("[data-slide-subtitle]");
      const mCaption = measurer.querySelector("[data-button-caption]");
      const mBtnWrap = measurer.querySelector(
        "[data-carousel-button-container]"
      );

      let maxH = 0;

      // If slide content varies by breakpoint (fluid type), measure all slides
      for (const slide of this.slides) {
        if (mTitle) mTitle.textContent = slide?.title || "";
        if (mSub) mSub.textContent = slide?.subtitle || "";
        if (mCaption) mCaption.textContent = slide?.buttonCaption || "";
        if (mBtnWrap)
          mBtnWrap.style.display = slide?.button?.text ? "" : "none";

        // Note: use scrollHeight (or offsetHeight) to include internal wrapping
        const h = measurer.scrollHeight;
        if (h > maxH) maxH = h;
      }

      // Fallback to current content height if slides missing
      if (!maxH) {
        maxH = this.contentArea.scrollHeight || 0;
      }

      // Apply as min-height so content can never push layout below
      this.contentArea.style.minHeight = `${maxH}px`;

      // Clean up
      document.body.removeChild(measurer);
    }

    setupEventListeners() {
      // Setup image click handlers
      this.imageElements.forEach((img, index) => {
        img.addEventListener("mouseenter", () => {
          if (this.isSideImage(index)) {
            this.stopAutoScroll();
          }
        });

        img.addEventListener("mouseleave", () => {
          if (this.isSideImage(index)) {
            this.startAutoScroll();
          }
        });

        img.addEventListener("click", () => {
          this.stopAutoScroll();
          if (!this.isSideImage(index) || this.isTransitioning) return;

          const isRightImage = index === this.imagePositions[2];
          this.changeSlide(isRightImage ? "next" : "previous");
        });
      });

      // Setup button hover handlers
      if (this.buttonElement) {
        this.buttonElement.addEventListener("mouseenter", () => {
          this.stopAutoScroll();
        });

        this.buttonElement.addEventListener("mouseleave", () => {
          this.startAutoScroll();
        });
      }

      // Setup cursor blinking
      if (this.cursor) {
        this.cursor.style.animation = `blink-cursor ${TIMING.ui.cursorBlink}ms steps(1, start) infinite`;
      }
    }

    isSideImage(imgIndex) {
      const [leftImg, , rightImg] = this.imagePositions;
      return imgIndex === leftImg || imgIndex === rightImg;
    }

    updateImageElements() {
      this.imageElements.forEach((img, index) => {
        const src = this.imageSources[index];
        if (src) {
          if (img.getAttribute("src") !== src) img.src = src;
        } else if (img.hasAttribute("src")) {
          img.removeAttribute("src");
        }
        const transition = this.imageTransitions[index];
        const transitionData = TRANSITIONS[transition];

        if (!transitionData) {
          console.error(
            `No transition data found for transition: ${transition}`
          );
          return;
        }

        img.className = `absolute inset-0 m-auto object-contain object-center ${transitionData.classes}`;

        // Each state owns the inline style outright. Clear what an earlier state
        // may have set (a scale-in delay, a 1100ms "all" duration) before applying
        // this one, otherwise it lingers into the stable states and mistimes the
        // next move: the React original replaced the style object wholesale.
        for (const prop of STATE_STYLE_PROPS) img.style[prop] = "";
        Object.assign(img.style, transitionData.style);

        // Set alt text
        const slideData = this.getSlideDataForImage(index);
        img.alt = slideData?.title || `Carousel slide ${index + 1}`;
      });
    }

    getSlideDataForImage(imgIndex) {
      const [leftImg, centerImg, rightImg, outImg] = this.imagePositions;
      const totalSlides = this.slides.length;
      const prevIndex =
        (this.currentSlideIndex - 1 + totalSlides) % totalSlides;
      const nextIndex = (this.currentSlideIndex + 1) % totalSlides;
      const outIndex = (this.currentSlideIndex + 2) % totalSlides;

      if (imgIndex === leftImg) return this.slides[prevIndex];
      if (imgIndex === centerImg) return this.slides[this.currentSlideIndex];
      if (imgIndex === rightImg) return this.slides[nextIndex];
      if (imgIndex === outImg) return this.slides[outIndex];

      return null;
    }

    updateContent() {
      const currentSlide = this.slides[this.currentSlideIndex];
      if (!currentSlide) return;

      if (this.titleElement) {
        this.titleElement.textContent = currentSlide.title || "";
      }

      if (this.subtitleElement) {
        this.subtitleElement.textContent = currentSlide.subtitle || "";
      }

      if (this.buttonCaptionElement) {
        this.buttonCaptionElement.textContent =
          currentSlide.buttonCaption || "";
      }

      this.updateButton(currentSlide.button);
    }

    updateButton(buttonData) {
      if (!this.buttonContainer || !this.buttonElement || !buttonData?.text) {
        if (this.buttonContainer) {
          this.buttonContainer.style.display = "none";
        }
        return;
      }

      // Update button attributes
      this.buttonElement.href = buttonData.url || "#";
      this.buttonElement.setAttribute("aria-label", buttonData.text);

      // Update button variant classes
      const baseClasses = "btn btn-md btn-icon-end";
      const variantClass = `btn-${buttonData.style || "secondary"}`;
      this.buttonElement.className = `${baseClasses} ${variantClass} carousel-button`;

      // Update button text content (find the text span within the button)
      const textSpan = this.buttonElement.querySelector("span span");
      if (textSpan) {
        textSpan.textContent = buttonData.text;
      }

      // Show button container
      this.buttonContainer.style.display = "block";
    }

    // Typing animation functions
    async delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    typingDelay() {
      return (
        Math.random() * (TIMING.typing.max - TIMING.typing.min) +
        TIMING.typing.min
      );
    }

    async backspaceText(text) {
      if (!text || !this.bgTextElement) return;

      return new Promise((resolve) => {
        let currentText = text;

        const removeLastCharacter = () => {
          currentText = currentText.slice(0, -1);
          this.bgTextElement.textContent = currentText;

          if (currentText.length === 0) {
            resolve();
          } else {
            setTimeout(removeLastCharacter, this.typingDelay());
          }
        };

        removeLastCharacter();
      });
    }

    async typeText(typingText) {
      if (!typingText || !this.bgTextElement) return;

      return new Promise((resolve) => {
        let index = 0;

        const typeChar = () => {
          index++;
          const isLast = index >= typingText.length;
          this.bgTextElement.textContent = typingText.slice(0, index);

          if (isLast) {
            this.bgText = typingText;
            resolve();
          } else {
            setTimeout(typeChar, this.typingDelay());
          }
        };

        typeChar();
      });
    }

    // Animation sequence functions
    async performExitAnimations(
      scalingOutImg,
      scalingOutTransition,
      centerImg,
      centerTransition
    ) {
      this.transitionContent = true;

      if (this.contentArea) {
        this.contentArea.style.opacity = "0";
      }

      const backspacePromise = this.backspaceText(this.bgText);

      this.imageTransitions[scalingOutImg] = scalingOutTransition;
      this.imageTransitions[centerImg] = centerTransition;
      this.updateImageElements();

      // Wait for the text to backspace AND the exiting image to finish scaling
      // out; a short title backspaces faster than the scale-out, and the image
      // used to jump to the far side while still visible.
      await Promise.all([
        backspacePromise,
        this.delay(TIMING.transition.scaleOut),
      ]);
    }

    async performRepositionAnimations(repositionImg, repositionTransition) {
      await this.delay(TIMING.sequence.beforePositionUpdate);

      this.imageTransitions[repositionImg] = repositionTransition;
      this.updateImageElements();
    }

    async performEntryAnimations(
      newSlideIndex,
      movingImg,
      scalingImg,
      scalingTransition
    ) {
      this.transitionContent = false;
      const targetSlide = this.slides[newSlideIndex];
      const typePromise = this.typeText(targetSlide?.bgText || "");

      this.imageTransitions[movingImg] = "to-center";
      this.imageTransitions[scalingImg] = scalingTransition;
      this.updateImageElements();

      this.currentSlideIndex = newSlideIndex;
      this.updateContent();

      if (this.contentArea) {
        this.contentArea.style.opacity = "1";
      }

      await Promise.all([
        typePromise,
        this.delay(TIMING.transition.inDuration),
      ]);
    }

    resetImageState(outImg, futureSlideIndex, newPositions) {
      const futureSlide = this.slides[futureSlideIndex];
      this.imageSources[outImg] = futureSlide?.image || "";
      this.imagePositions = newPositions;

      const [newLeft, newCenter, newRight, newOut] = newPositions;
      this.imageTransitions = {
        [newLeft]: "left-stable",
        [newCenter]: "center-stable",
        [newRight]: "right-stable",
        [newOut]: "out-stable",
      };

      this.updateImageElements();
    }

    async transitionToPreviousSlide() {
      if (this.slides.length <= 1) return;

      const [leftImg, centerImg, rightImg, outImg] = this.imagePositions;
      const totalSlides = this.slides.length;
      const prevSlideIndex =
        (this.currentSlideIndex - 1 + totalSlides) % totalSlides;
      const pastSlideIndex = (prevSlideIndex - 2 + totalSlides) % totalSlides;

      await this.performExitAnimations(
        rightImg,
        "right-scaling-out",
        centerImg,
        "center-to-right"
      );
      await this.performRepositionAnimations(rightImg, "right-to-left");
      await this.performEntryAnimations(
        prevSlideIndex,
        leftImg,
        outImg,
        "left-scaling-in"
      );

      const newPositions = [outImg, leftImg, centerImg, rightImg];

      await this.delay(TIMING.sequence.finalReset);
      this.resetImageState(rightImg, pastSlideIndex, newPositions);
    }

    async transitionToNextSlide() {
      if (this.slides.length <= 1) return;

      const [leftImg, centerImg, rightImg, outImg] = this.imagePositions;
      const totalSlides = this.slides.length;
      const nextSlideIndex = (this.currentSlideIndex + 1) % totalSlides;
      const futureSlideIndex = (nextSlideIndex + 2) % totalSlides;

      await this.performExitAnimations(
        leftImg,
        "left-scaling-out",
        centerImg,
        "center-to-left"
      );
      await this.performRepositionAnimations(leftImg, "left-to-right");
      await this.performEntryAnimations(
        nextSlideIndex,
        rightImg,
        outImg,
        "right-scaling-in"
      );

      const newPositions = [centerImg, rightImg, outImg, leftImg];

      await this.delay(TIMING.sequence.finalReset);
      this.resetImageState(leftImg, futureSlideIndex, newPositions);
    }

    async changeSlide(direction) {
      if (this.isTransitioning) return;

      this.isTransitioning = true;

      try {
        if (direction === "next") {
          await this.transitionToNextSlide();
        } else {
          await this.transitionToPreviousSlide();
        }
      } finally {
        this.isTransitioning = false;
        // A running auto-scroll counts its next advance from the end of this
        // change, so a slide never flips again moments after it settled.
        if (this.autoScrollInterval) this.startAutoScroll();
      }
    }

    start() {
      if (this.slides.length === 0) return;

      // Initialize background text
      this.typeText(this.slides[0]?.bgText || "");

      // Start auto-scroll
      this.startAutoScroll();
    }

    startAutoScroll() {
      if (this.slides.length <= 1) return;

      // Only ever one interval: hover in/out on the side images and the button
      // call this repeatedly, and stacked intervals advanced the carousel at
      // random moments.
      this.stopAutoScroll();
      this.autoScrollInterval = setInterval(() => {
        if (this.autoScrollEnabled) {
          this.changeSlide("next");
        }
      }, TIMING.ui.autoScroll);
    }

    stopAutoScroll() {
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
        this.autoScrollInterval = null;
      }
    }

    destroy() {
      this.stopAutoScroll();
      if (this.gridCleanup) {
        this.gridCleanup();
      }
      if (this.contentSizerCleanup) {
        this.contentSizerCleanup();
      }
    }
  }

  // Add CSS for cursor blinking animation
  const style = document.createElement("style");
  style.textContent = `
    @keyframes blink-cursor {
      0%, 49% { opacity: 1; }
      50%, 100% { opacity: 0; }
    }
    @media (prefers-reduced-motion: reduce) {
      .typing-cursor {
        animation: none !important;
        opacity: 1 !important;
      }
    }
  `;
  document.head.appendChild(style);

  // Initialize carousel when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      new GridCarousel();
    });
  } else {
    new GridCarousel();
  }
})();
